/**
 * Encrypt Ed25519 secret key for server-side backup (AES-256-GCM + PBKDF2-SHA256).
 */
import { u8ToB64, b64ToU8 } from "./crypto";

const PBKDF2_ITERATIONS = 100_000;

async function deriveAesKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as unknown as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Random 16-byte salt for PBKDF2 (store in DB as base64). */
export function randomKeySalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

export async function encryptSecretKeyBackup(secretKeyEd: Uint8Array, password: string, salt: Uint8Array): Promise<string> {
  const key = await deriveAesKey(password, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, secretKeyEd as unknown as BufferSource),
  );
  const out = new Uint8Array(iv.length + ciphertext.length);
  out.set(iv, 0);
  out.set(ciphertext, iv.length);
  return u8ToB64(out);
}

export async function decryptSecretKeyBackup(
  encryptedB64: string,
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const buf = b64ToU8(encryptedB64);
  if (buf.length < 12 + 16) {
    throw new Error("invalid encrypted backup");
  }
  const iv = buf.slice(0, 12);
  const ct = buf.slice(12);
  const key = await deriveAesKey(password, salt);
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return new Uint8Array(decrypted);
}
